import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Toaster } from 'sonner';

import { useAuth } from '@/providers/AuthProvider';
import { AppShell } from '@/marketing/components/layout/AppShell';
import { ComingSoonPage } from '@/marketing/components/layout/ComingSoonPage';
import { BrandManagerPage } from '@/marketing/components/brand/BrandManagerPage';
import { CommunityPage } from '@/marketing/components/community/CommunityPage';
import { InfluencerSearchPage } from '@/marketing/components/influencer/InfluencerSearchPage';
import { CampaignManagerPage } from '@/marketing/components/influencerCampaign/CampaignManagerPage';
import { AffiliatePage } from '@/marketing/components/affiliate/AffiliatePage';
import { CampaignDetailPage } from '@/marketing/components/influencerCampaign/detail/CampaignDetailPage';
import { useAuthStore } from '@/marketing/stores/useAuthStore';
import MarketingErrorBoundary from '@/marketing/MarketingErrorBoundary';

/** Feeds the admin session into the marketing zustand store so it needs no second login. */
function MarketingAuthBridge() {
  const { user } = useAuth();
  const syncFromAdmin = useAuthStore((s) => s.syncFromAdmin);

  useEffect(() => {
    syncFromAdmin(user);
  }, [user, syncFromAdmin]);

  return null;
}

function CampaignDetailRoute() {
  const { campaignId } = useParams<{ campaignId: string }>();
  if (!campaignId) return <Navigate to=".." replace />;
  return <CampaignDetailPage campaignId={campaignId} />;
}

/** Height of the marketing shell: the viewport minus whatever admin chrome sits above it. */
function useAvailableHeight() {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>('100vh');

  useLayoutEffect(() => {
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top ?? 0;
      setHeight(`${Math.max(0, window.innerHeight - top)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return { ref, height };
}

/** Mounts the whole marketing admin under the dashboard's /marketing menu. */
export default function MarketingSection() {
  const { ref: shellRef, height } = useAvailableHeight();

  return (
    <>
      <MarketingAuthBridge />
      {/* The marketing pages toast through sonner, while the rest of the admin mounts
       * react-hot-toast in App.tsx; without this host every marketing success/error
       * toast (e.g. a failed stage transition) is silently dropped. */}
      <Toaster position="top-center" richColors closeButton />
      {/* AppShell and its sidebar are h-screen by design; cap them to whatever the admin
       * chrome leaves, measured rather than hardcoded — the navbar height is not fixed. */}
      <div
        ref={shellRef}
        className="ss-marketing tw-overflow-hidden [&>div]:tw-h-full [&_aside]:tw-h-full"
        style={{ height }}
      >
        <AppShell>
          <MarketingErrorBoundary>
            <Routes>
              <Route index element={<InfluencerSearchPage />} />
              <Route path="brand-manager" element={<BrandManagerPage />} />
              <Route path="campaign-matcher" element={<CampaignManagerPage />} />
              <Route path="community" element={<CommunityPage />} />
              <Route path="affiliate" element={<AffiliatePage />} />
              <Route
                path="template-manager"
                element={<ComingSoonPage label="Template Manager" icon={FileText} />}
              />
              <Route path="influencer-campaigns/:campaignId" element={<CampaignDetailRoute />} />
            </Routes>
          </MarketingErrorBoundary>
        </AppShell>
      </div>
    </>
  );
}
